import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { CircularProgress, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button } from "@mui/material";
import { 
  FaCamera, FaUpload, FaCheckCircle, FaChevronRight, FaChevronLeft, 
  FaTimes, FaInfoCircle, FaTrashAlt, FaFingerprint, FaArrowRight,
  FaFileAlt, FaShieldAlt
} from "react-icons/fa";
import { checkAadhar } from "../../Api/candidateApi";
import { extractAadhaarFromImage, extractDlFromImage, validateVerhoeff } from "../utils/clientOcrParser";
import { logClientAction } from "../../Api/logApi";

const safeLogClientAction = (action, module, metadata = {}) => {
  logClientAction(action, module, metadata).catch((err) => {
    console.error("Action Log Error:", err);
  });
};

const validateAadhaar = (aadhaarString) => {
  if (!aadhaarString) return false;
  const clean = String(aadhaarString).replace(/\D/g, "").trim();
  if (clean.length !== 12) return false;
  return validateVerhoeff(clean);
};

// =====================================================================
// DESIGN SYSTEM CSS
// =====================================================================
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600;700&family=Inter:wght@400;500;600;700;800&display=swap');

  :root {
    // --wb-bg: #F6F8FA;
    --wb-card: #FFFFFF;
    --wb-border: #E2E8F0;
    --wb-text: #0F172A;
    --wb-muted: #64748B;
    --wb-teal: #167C80;
    --wb-teal-soft: #E6F4F4;
    --wb-blue: #2563EB;
    --wb-blue-soft: #EFF6FF;
    --wb-success: #16A34A;
    --wb-warning: #D97706;
    --wb-danger: #DC2626;
  }

  .dash-root {
    font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    background-color: var(--wb-bg);
    min-height: 100vh;
    color: var(--wb-text);
    padding: 28px 36px;
    box-sizing: border-box;
  }
  .dash-root * { box-sizing: border-box; }

  /* App Header */
  .app-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
    padding-bottom: 18px;
    border-bottom: 1px solid var(--wb-border);
  }

  .app-tag {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 11px;
    font-weight: 700;
    color: var(--wb-teal);
    letter-spacing: 0.14em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
  }
  .app-tag::before {
    content: '';
    width: 12px;
    height: 2px;
    background: var(--wb-teal);
    display: inline-block;
  }

  .app-title {
    font-size: 24px;
    font-weight: 800;
    color: var(--wb-text);
    letter-spacing: -0.02em;
    margin-bottom: 2px;
  }
  .app-subtitle {
    font-size: 13px;
    color: var(--wb-muted);
  }

  .engine-badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    padding: 6px 12px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    color: var(--wb-text);
    margin-right: 12px;
  }
  .status-dot-green {
    width: 8px;
    height: 8px;
    background: var(--wb-success);
    border-radius: 50%;
    box-shadow: 0 0 6px rgba(22, 163, 74, 0.4);
  }

  .btn-compact-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    color: var(--wb-text);
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 12.5px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .btn-compact-back:hover {
    background: #F1F5F9;
    border-color: #CBD5E1;
  }

  /* Workflow Navigator */
  .workflow-nav {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 24px;
  }
  @media (max-width: 768px) {
    .workflow-nav { grid-template-columns: 1fr; }
  }

  .workflow-step {
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    border-radius: 8px;
    padding: 14px 18px;
    display: flex;
    align-items: center;
    gap: 14px;
    position: relative;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .workflow-step.active {
    border-color: var(--wb-teal);
    background: #FFFFFF;
    box-shadow: 0 2px 8px rgba(22, 124, 128, 0.08);
  }
  .workflow-step.completed {
    border-color: #CBD5E1;
    background: #FAFAFA;
  }

  .workflow-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #CBD5E1;
    transition: all 0.2s ease;
    flex-shrink: 0;
  }
  .workflow-step.active .workflow-dot {
    background: var(--wb-teal);
    box-shadow: 0 0 0 3px var(--wb-teal-soft);
  }
  .workflow-step.completed .workflow-dot {
    background: var(--wb-teal);
  }

  .workflow-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--wb-text);
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .workflow-sub {
    font-size: 11.5px;
    color: var(--wb-muted);
  }

  /* Container */
  .workbench-container {
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    border-radius: 10px;
    padding: 28px;
    box-shadow: 0 1px 3px rgba(15, 23, 42, 0.04);
  }

  .section-banner {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding-bottom: 16px;
    border-bottom: 1px solid var(--wb-border);
    margin-bottom: 20px;
  }
  .section-heading {
    font-size: 15px;
    font-weight: 800;
    color: var(--wb-text);
    letter-spacing: 0.04em;
    text-transform: uppercase;
    font-family: 'IBM Plex Mono', monospace;
  }
  .section-desc {
    font-size: 13px;
    color: var(--wb-muted);
    margin-top: 2px;
  }

  /* Trays Grid */
  .tray-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
    margin-bottom: 24px;
  }
  @media (max-width: 640px) {
    .tray-grid { grid-template-columns: 1fr; }
  }

  .document-tray {
    background: #F8FAFC;
    border: 1px solid var(--wb-border);
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 14px;
    transition: all 0.2s ease;
  }
  .document-tray.captured {
    background: #FFFFFF;
    border-color: var(--wb-teal);
  }

  .tray-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .tray-title {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    color: var(--wb-text);
    text-transform: uppercase;
  }
  .tray-badge-ready {
    font-size: 11px;
    font-weight: 700;
    color: var(--wb-muted);
    background: #E2E8F0;
    padding: 3px 9px;
    border-radius: 4px;
  }
  .tray-badge-captured {
    font-size: 11px;
    font-weight: 700;
    color: var(--wb-teal);
    background: var(--wb-teal-soft);
    padding: 3px 9px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .tray-viewzone {
    height: 190px;
    background: #FFFFFF;
    border: 1px dashed #CBD5E1;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
  }

  .btn-tray-scan {
    flex: 1;
    background: var(--wb-teal);
    color: #FFFFFF;
    border: none;
    border-radius: 6px;
    padding: 10px;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-tray-scan:hover { background: #126366; }

  .btn-tray-upload {
    flex: 1;
    background: #2563EB;
    color: #FFFFFF;
    border: none;
    border-radius: 6px;
    padding: 10px;
    font-size: 12px;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    cursor: pointer;
    transition: background 0.15s;
  }
  .btn-tray-upload:hover { background: #1D4ED8; }

  /* Camera Live View */
  .camera-view {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 180px;
    border-radius: 6px;
    overflow: hidden;
    background: #000;
    cursor: pointer;
  }
  @keyframes laserScan {
    0% { top: 4%; opacity: 0.8; }
    50% { top: 88%; opacity: 1; }
    100% { top: 4%; opacity: 0.8; }
  }
  @keyframes pulseGlow {
    0% { opacity: 0.4; }
    50% { opacity: 0.9; }
    100% { opacity: 0.4; }
  }
  .scan-hud-overlay {
    position: absolute;
    inset: 0;
    pointer-events: none;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-between;
    padding: 12px;
    background: radial-gradient(circle, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.4) 100%);
    z-index: 5;
  }
  .scan-laser-line {
    position: absolute;
    left: 4%; right: 4%;
    height: 3px;
    background: linear-gradient(90deg, rgba(22,124,128,0) 0%, #167C80 50%, rgba(22,124,128,0) 100%);
    box-shadow: 0 0 12px #167C80, 0 0 20px #167C80;
    animation: laserScan 2.2s ease-in-out infinite;
    z-index: 6;
  }
  .scan-corner {
    position: absolute;
    width: 20px; height: 20px;
    border-color: #167C80;
    border-style: solid;
    z-index: 6;
  }
  .scan-corner-tl { top: 10px; left: 10px; border-width: 3px 0 0 3px; border-top-left-radius: 4px; }
  .scan-corner-tr { top: 10px; right: 10px; border-width: 3px 3px 0 0; border-top-right-radius: 4px; }
  .scan-corner-bl { bottom: 10px; left: 10px; border-width: 0 0 3px 3px; border-bottom-left-radius: 4px; }
  .scan-corner-br { bottom: 10px; right: 10px; border-width: 0 3px 3px 0; border-bottom-right-radius: 4px; }
  .scan-badge {
    background: rgba(15, 23, 42, 0.85);
    color: #2BB5CE;
    border: 1px solid rgba(43, 181, 206, 0.4);
    backdrop-filter: blur(4px);
    padding: 4px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 700;
    font-family: 'IBM Plex Mono', monospace;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    display: flex;
    align-items: center;
    gap: 6px;
    animation: pulseGlow 1.5s infinite;
  }

  /* Extract & Results Bar */
  .extraction-bar {
    background: #F8FAFC;
    border: 1px solid var(--wb-border);
    border-radius: 8px;
    padding: 16px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
  }

  .btn-extract-primary {
    background: var(--wb-teal);
    color: #FFFFFF;
    border: none;
    border-radius: 6px;
    padding: 12px 24px;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0.04em;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .btn-extract-primary:hover:not(:disabled) {
    background: #126366;
  }
  .btn-extract-primary:disabled {
    background: #CBD5E1;
    cursor: not-allowed;
  }

  .quick-result-strip {
    background: #FFFFFF;
    border: 1px solid var(--wb-teal);
    border-radius: 8px;
    padding: 14px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 16px;
  }

  /* Review Layout */
  .review-summary-box {
    background: #F8FAFC;
    border: 1px solid var(--wb-border);
    border-radius: 8px;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  .review-grid {
    display: grid;
    grid-template-columns: 65% 35%;
    gap: 24px;
  }
  @media (max-width: 900px) {
    .review-grid { grid-template-columns: 1fr; }
  }

  .review-panel {
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    border-radius: 8px;
    padding: 20px;
  }
  .review-panel-title {
    font-family: 'IBM Plex Mono', monospace;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--wb-text);
    padding-bottom: 12px;
    border-bottom: 1px solid var(--wb-border);
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .input-field {
    width: 100%;
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    border-radius: 6px;
    padding: 9px 12px;
    font-size: 13.5px;
    color: var(--wb-text);
    font-family: 'Inter', sans-serif;
    outline: none;
    transition: border-color 0.15s;
  }
  .input-field:focus {
    border-color: var(--wb-teal);
    box-shadow: 0 0 0 3px var(--wb-teal-soft);
  }

  .segmented-gender {
    display: flex;
    gap: 6px;
    background: #F1F5F9;
    padding: 4px;
    border-radius: 6px;
    border: 1px solid var(--wb-border);
  }
  .segmented-btn {
    flex: 1;
    border: none;
    padding: 8px 12px;
    font-size: 12.5px;
    font-weight: 600;
    border-radius: 4px;
    background: transparent;
    color: var(--wb-muted);
    cursor: pointer;
    transition: all 0.15s ease;
  }
  .segmented-btn.active {
    background: var(--wb-teal);
    color: #FFFFFF;
    box-shadow: 0 1px 3px rgba(22, 124, 128, 0.2);
  }

  /* Bottom Action Bar */
  .bottom-sticky-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 28px;
    padding-top: 18px;
    border-top: 1px solid var(--wb-border);
  }

  .btn-action-outline {
    background: #FFFFFF;
    border: 1px solid var(--wb-border);
    color: var(--wb-text);
    padding: 10px 18px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: all 0.15s ease;
  }
  .btn-action-outline:hover {
    background: #F8FAFC;
    border-color: #CBD5E1;
  }

  .btn-action-primary {
    background: var(--wb-teal);
    color: #FFFFFF;
    border: none;
    padding: 11px 24px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: all 0.15s ease;
  }
  .btn-action-primary:hover {
    background: #126366;
  }

  /* Modal */
  .modal-overlay {
    position: fixed; top:0; left:0; right:0; bottom:0;
    background: rgba(15, 23, 42, 0.65);
    backdrop-filter: blur(4px);
    display: flex; align-items: center; justify-content: center;
    z-index: 99999; padding: 20px;
  }
  .modal-card {
    background: #FFFFFF;
    border-radius: 12px;
    padding: 28px;
    max-width: 540px;
    width: 100%;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
    border: 1px solid var(--wb-border);
    position: relative;
  }
  .modal-close {
    position: absolute; top: 16px; right: 16px;
    width: 28px; height: 28px; border-radius: 50%;
    background: #F1F5F9; border: none;
    color: var(--wb-muted); cursor: pointer;
    display: flex; align-items: center; justify-content: center;
  }
`;

// Helper: Gender detection from OCR lines
const detectGenderFromLines = (lines = []) => {
  const text = lines.join(" ").toLowerCase();
  if (/\bfemale\b/.test(text)) return "Female";
  if (/\bmale\b/.test(text)) return "Male";
  if (/\bother\b/.test(text)) return "Other";
  return "";
};

// Helper: Check if string is suspicious DL label rather than candidate name
const isSuspiciousDlName = (name) => {
  if (!name || typeof name !== "string") return true;
  const s = name.trim().toLowerCase();
  if (!s || s.length < 3) return true;
  const invalidKeywords = [
    "holder's signature", "holders signature", "signature of holder", "signature",
    "badge date", "badge no", "date of issue", "valid till", "date of birth",
    "address", "name", "son/daughter/wife of", "son of", "daughter of",
    "wife of", "s/o", "d/o", "w/o", "issuing authority", "licence", "license",
    "driving", "union of india", "transport", "government", "form"
  ];
  return invalidKeywords.some(kw => s.includes(kw)) || /^\d+$/.test(s);
};

// --- Parse Aadhaar single image response ---
const parseAadhaarResponse = (response) => {
  const rawFields = response?.data?.fields || response?.fields || response?.data || response || {};
  const ocrLines = response?.data?.lines || response?.lines || response?.rawLines || [];
  const lineTexts = ocrLines.map(line => (typeof line === "string" ? line.trim() : line?.text?.trim() || "")).filter(Boolean);

  let aadhaarNumber = rawFields.aadhaar_number || rawFields.aadharNumber || "";
  if (aadhaarNumber) {
    aadhaarNumber = String(aadhaarNumber).replace(/\s+/g, "").trim();
  }

  const candidateName = rawFields.full_name || rawFields.name || "";

  let address = rawFields.address_line || rawFields.address || "";
  if (!address && lineTexts.length > 0) {
    const addressKeywords = ["no.", "road", "street", "nagar", "patti", "katpadi", "vellore", "tamil nadu", "dharapadavedu", "house", "village", "dist", "district", "post"];
    const matchedAddressLines = lineTexts.filter(line => {
      const lower = line.toLowerCase();
      return addressKeywords.some(kw => lower.includes(kw)) || /\d{6}/.test(line);
    });
    if (matchedAddressLines.length > 0) {
      address = matchedAddressLines.join(", ");
    }
  }

  const extractFatherName = (rawFather, addrText, lines) => {
    if (rawFather && rawFather.trim().length >= 3) {
      return rawFather.trim();
    }
    const combined = [addrText, ...lines].join(" ");
    const match = combined.match(/(?:S\/O|D\/O|W\/O|C\/O|S\/o|D\/o|W\/o|C\/o|SO|DO|WO|CO|Son\s+of|Daughter\s+of|Wife\s+of|Care\s+of|Father(?:\s+Name)?)\s*[:\-\s]+\s*([A-Za-z\s\.]{3,40})/i);
    if (match && match[1]) {
      let cand = match[1].split(/[,;\n\d]/)[0].trim();
      cand = cand.replace(/^(Photo|Mr|Shri|Late)\s+/i, "").trim();
      if (cand.length >= 3 && !/^(address|road|street|nagar|house|village|patti)$/i.test(cand)) {
        return cand;
      }
    }
    return "";
  };

  const fatherName = extractFatherName(
    rawFields.father_name || rawFields.fathersName || "",
    address,
    lineTexts
  );

  let dob = rawFields.date_of_birth || rawFields.dob || "";
  if (dob && /^\d{2}\/\d{2}\/\d{4}$/.test(dob)) {
    const [day, month, year] = dob.split("/");
    dob = `${year}-${month}-${day}`;
  }

  const pincode = rawFields.pincode || rawFields.pinCode || "";

  const mobileMatch = lineTexts.join(" ").match(/(?<!\d)[6-9]\d{9}(?!\d)/);
  const mobileNumber = rawFields.mobile_number || rawFields.contactNumber || mobileMatch?.[0] || "";

  let gender = rawFields.gender || "";
  if (gender) {
    const gLower = String(gender).toLowerCase();
    if (gLower.includes("female")) gender = "Female";
    else if (gLower.includes("male")) gender = "Male";
    else if (gLower.includes("other")) gender = "Other";
    else gender = "";
  }
  if (!gender) {
    gender = detectGenderFromLines(lineTexts);
  }

  const rawFieldConfidence = rawFields.field_confidence || {};
  const overallConfidence = rawFields.confidence;

  const getConfScore = (fieldName, val) => {
    if (!val) return 0;
    const fieldConf = rawFieldConfidence[fieldName] !== undefined ? rawFieldConfidence[fieldName] : overallConfidence;
    if (typeof fieldConf === "number") return fieldConf;
    return 0.7;
  };

  return {
    fields: {
      aadharNumber: aadhaarNumber,
      name: candidateName,
      fathersName: fatherName,
      dob: dob,
      gender: gender,
      contactNumber: mobileNumber,
      address: address,
      pincode: pincode
    },
    scores: {
      aadharNumber: getConfScore("aadhaar_number", aadhaarNumber),
      name: getConfScore("full_name", candidateName),
      fathersName: getConfScore("father_name", fatherName),
      dob: getConfScore("date_of_birth", dob),
      gender: getConfScore("gender", gender),
      contactNumber: getConfScore("mobile_number", mobileNumber),
      address: getConfScore("address_line", address),
      pincode: getConfScore("pincode", pincode)
    }
  };
};

// Merge Aadhaar Front + Back parsed objects
const mergeAadhaarOcrData = (frontParsed, backParsed) => {
  if (!backParsed) return frontParsed;

  const fFields = frontParsed.fields;
  const bFields = backParsed.fields;
  const fScores = frontParsed.scores;
  const bScores = backParsed.scores;

  const mergedFields = {};
  const mergedScores = {};

  const pickField = (key, preferFrontIdentity = false) => {
    const fVal = fFields[key] || "";
    const bVal = bFields[key] || "";
    const fScore = fScores[key] || 0;
    const bScore = bScores[key] || 0;

    if (preferFrontIdentity) {
      if (fVal && fVal.trim() !== "") {
        mergedFields[key] = fVal;
        mergedScores[key] = fScore;
      } else {
        mergedFields[key] = bVal;
        mergedScores[key] = bScore;
      }
    } else {
      if (fVal && !bVal) {
        mergedFields[key] = fVal;
        mergedScores[key] = fScore;
      } else if (!fVal && bVal) {
        mergedFields[key] = bVal;
        mergedScores[key] = bScore;
      } else if (fVal && bVal) {
        if (bVal.length > fVal.length || bScore > fScore) {
          mergedFields[key] = bVal;
          mergedScores[key] = bScore;
        } else {
          mergedFields[key] = fVal;
          mergedScores[key] = fScore;
        }
      } else {
        mergedFields[key] = "";
        mergedScores[key] = 0;
      }
    }
  };

  pickField("aadharNumber", true);
  pickField("name", true);
  pickField("fathersName", true);
  pickField("dob", true);
  pickField("gender", true);

  pickField("contactNumber", false);
  pickField("address", false);
  pickField("pincode", false);

  return { fields: mergedFields, scores: mergedScores };
};

// --- Parse DL single image response ---
const parseDlResponse = (response) => {
  const rawFields = response?.data?.fields || response?.fields || response?.data || response || {};
  const ocrLines = response?.data?.lines || response?.lines || response?.rawLines || [];
  const lineTexts = ocrLines.map(line => (typeof line === "string" ? line.trim() : line?.text?.trim() || "")).filter(Boolean);

  const extractDLNumberFromLines = (lines) => {
    for (const line of lines) {
      const match = line.match(/\b([A-Z]{2}[0-9]{2}\s?[0-9]{11,13})\b/i) || line.match(/TN23\s?2020\d+/i);
      if (match) {
        return match[0].replace(/\s+/g, "").replace(/^\//, "").trim();
      }
    }
    return "";
  };

  const extractNameFromDL = (lines) => {
    const nameIndex = lines.findIndex(text => /^name$/i.test(text));
    if (nameIndex !== -1) {
      for (let i = nameIndex + 1; i < Math.min(nameIndex + 4, lines.length); i++) {
        const candidate = lines[i];
        if (
          candidate &&
          !isSuspiciousDlName(candidate) &&
          !/^\d{1,2}[-/]\d{1,2}[-/]\d{4}$/.test(candidate)
        ) {
          return candidate;
        }
      }
    }
    return "";
  };

  const extractDOBFromDL = (lines) => {
    const dobLabelIndex = lines.findIndex(text => /^date of birth$/i.test(text));
    if (dobLabelIndex !== -1) {
      for (let i = dobLabelIndex + 1; i < Math.min(dobLabelIndex + 4, lines.length); i++) {
        const match = lines[i]?.match(/\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/);
        if (match) return match[0];
      }
    }
    return "";
  };

  const extractIssueDateFromDL = (lines) => {
    const index = lines.findIndex(text => /^date of issue$/i.test(text));
    if (index !== -1) {
      for (let i = index + 1; i < Math.min(index + 4, lines.length); i++) {
        const match = lines[i]?.match(/\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/);
        if (match) return match[0];
      }
    }
    return "";
  };

  const extractExpiryDateFromDL = (lines) => {
    const index = lines.findIndex(text => /^valid till$/i.test(text));
    if (index !== -1) {
      for (let i = index + 1; i < Math.min(index + 4, lines.length); i++) {
        const match = lines[i]?.match(/\b\d{1,2}[-/]\d{1,2}[-/]\d{4}\b/);
        if (match) return match[0];
      }
    }
    return "";
  };

  const extractFatherNameFromDL = (lines) => {
    const index = lines.findIndex(text => /Son\/Daughter\/Wife of/i.test(text));
    if (index !== -1) {
      for (let i = index + 1; i < Math.min(index + 4, lines.length); i++) {
        const candidate = lines[i];
        if (
          candidate &&
          !isSuspiciousDlName(candidate)
        ) {
          return candidate;
        }
      }
    }
    return "";
  };

  const extractAuthorityFromDL = (lines) => {
    for (const line of lines) {
      if (/RTO\b/i.test(line)) {
        return line;
      }
    }
    return "";
  };

  let dlNo = rawFields.dlNo || rawFields.dl_number || extractDLNumberFromLines(lineTexts);
  if (dlNo) {
    dlNo = String(dlNo).replace(/^DL\.?\s*No\.?\s*/i, "").replace(/^\//, "").replace(/\s+/g, "").trim();
  }

  let dlCandidateName = (rawFields.name_on_dl && !isSuspiciousDlName(rawFields.name_on_dl))
    ? rawFields.name_on_dl
    : extractNameFromDL(lineTexts);

  let dlDob = rawFields.dob || extractDOBFromDL(lineTexts) || "";
  let dlFatherName = rawFields.father_name || extractFatherNameFromDL(lineTexts) || "";

  // Prioritize direct backend parsed dates (including transport/HMV issue date and expiry date)
  let dlIssueDate = rawFields.dlIssueDate || rawFields.issue_date || extractIssueDateFromDL(lineTexts) || "";
  let licenseExpiryDate = rawFields.licenseExpiryDate || rawFields.validity_nt_or_transport || rawFields.expiry_date || extractExpiryDateFromDL(lineTexts) || "";

  let dlType = "";
  if (rawFields.dlType) {
    dlType = rawFields.dlType;
  } else if (rawFields.dl_type) {
    dlType = rawFields.dl_type;
  } else if (Array.isArray(rawFields.vehicle_classes) && rawFields.vehicle_classes.length > 0) {
    dlType = rawFields.vehicle_classes.map(vc => (typeof vc === "object" ? vc.class || vc.type || "" : vc)).filter(Boolean).join(", ");
  } else if (Array.isArray(rawFields.vehicle_class_entries) && rawFields.vehicle_class_entries.length > 0) {
    dlType = rawFields.vehicle_class_entries.map(item => item?.class).filter(Boolean).join(", ");
  } else {
    dlType = rawFields.vehicle_classes || "";
  }
  if (!dlType) {
    const mcwgLine = lineTexts.find(line => /^(MCWG|LMV|3W-CAB|TRANS)\b/i.test(line));
    if (mcwgLine) dlType = mcwgLine;
  }

  let dlAuthority = rawFields.rto_name || rawFields.rto_guess || rawFields.rto_code || extractAuthorityFromDL(lineTexts) || rawFields.issuing_authority || rawFields.issuing_state || "";

  const rawFieldConfidence = rawFields.field_confidence || {};
  const overallConfidence = rawFields.confidence;

  const getConfScore = (fieldName, val) => {
    if (!val) return 0;
    const fieldConf = rawFieldConfidence[fieldName] !== undefined ? rawFieldConfidence[fieldName] : overallConfidence;
    if (typeof fieldConf === "number") return fieldConf;
    return 0.7;
  };

  let vehicleClassesList = [];
  if (Array.isArray(rawFields.vehicle_classes)) {
    vehicleClassesList = rawFields.vehicle_classes.map(vc => (typeof vc === "object" ? vc.class || vc.type || "" : String(vc))).filter(Boolean);
  } else if (Array.isArray(rawFields.vehicle_class_entries)) {
    vehicleClassesList = rawFields.vehicle_class_entries.map(item => item?.class).filter(Boolean);
  }

  let vehicleClassEntriesList = Array.isArray(rawFields.vehicle_class_entries) ? rawFields.vehicle_class_entries : [];

  return {
    dlFields: {
      dlNo,
      dlType,
      vehicle_classes: vehicleClassesList,
      vehicle_class_entries: vehicleClassEntriesList,
      dlIssueDate,
      licenseExpiryDate,
      dlAuthority
    },
    dlScores: {
      dlNo: getConfScore("dl_number", dlNo),
      dlType: getConfScore("vehicle_classes", dlType),
      dlIssueDate: getConfScore("issue_date", dlIssueDate),
      licenseExpiryDate: getConfScore("validity_nt_or_transport", licenseExpiryDate),
      dlAuthority: getConfScore("rto_name", dlAuthority)
    },
    candidateExtra: {
      name: dlCandidateName,
      fathersName: dlFatherName,
      dob: dlDob
    }
  };
};

// Merge DL Front + Back parsed objects
const mergeDlOcrData = (frontParsed, backParsed) => {
  if (!backParsed) return frontParsed;

  const fDL = frontParsed.dlFields;
  const bDL = backParsed.dlFields;
  const fScores = frontParsed.dlScores;
  const bScores = backParsed.dlScores;

  const mergedDL = {};
  const mergedScores = {};

  const pickDlField = (key, preferFront = false) => {
    const fVal = fDL[key] || "";
    const bVal = bDL[key] || "";
    const fScore = fScores[key] || 0;
    const bScore = bScores[key] || 0;

    if (preferFront) {
      if (fVal && (typeof fVal !== "string" || fVal.trim() !== "")) {
        mergedDL[key] = fVal;
        mergedScores[key] = fScore;
      } else {
        mergedDL[key] = bVal;
        mergedScores[key] = bScore;
      }
    } else {
      if (fVal && !bVal) {
        mergedDL[key] = fVal;
        mergedScores[key] = fScore;
      } else if (!fVal && bVal) {
        mergedDL[key] = bVal;
        mergedScores[key] = bScore;
      } else if (fVal && bVal) {
        if (Array.isArray(fVal) || Array.isArray(bVal)) {
          mergedDL[key] = Array.isArray(fVal) && fVal.length > 0 ? fVal : bVal;
          mergedScores[key] = fScore || bScore;
        } else if (bScore > fScore || bVal.length > fVal.length) {
          mergedDL[key] = bVal;
          mergedScores[key] = bScore;
        } else {
          mergedDL[key] = fVal;
          mergedScores[key] = fScore;
        }
      } else {
        mergedDL[key] = Array.isArray(fVal) ? [] : "";
        mergedScores[key] = 0;
      }
    }
  };

  pickDlField("dlNo", true);
  pickDlField("dlType", false);
  pickDlField("vehicle_classes", false);
  pickDlField("vehicle_class_entries", false);
  pickDlField("dlIssueDate", false);
  pickDlField("licenseExpiryDate", false);
  pickDlField("dlAuthority", false);

  const candidateExtra = {
    name: (frontParsed.candidateExtra.name && !isSuspiciousDlName(frontParsed.candidateExtra.name))
      ? frontParsed.candidateExtra.name
      : backParsed.candidateExtra.name,
    fathersName: frontParsed.candidateExtra.fathersName || backParsed.candidateExtra.fathersName || "",
    dob: frontParsed.candidateExtra.dob || backParsed.candidateExtra.dob || ""
  };

  return { dlFields: mergedDL, dlScores: mergedScores, candidateExtra };
};

const scoreToStatus = (score, val, fieldKey = "") => {
  if (!val || String(val).trim() === "") {
    return { status: "Needs Verification", reason: "Field not detected" };
  }
  if (fieldKey === "aadharNumber" && !validateVerhoeff(String(val).replace(/\D/g, ""))) {
    return { status: "Needs Verification", reason: "Checksum mismatch" };
  }
  if (score >= 0.8) return { status: "High Confidence", reason: "" };
  if (score >= 0.6) return { status: "Medium Confidence", reason: "" };
  return { status: "Needs Verification", reason: "Low confidence check" };
};


export default function ScanAadhaarDL() {
  const navigate = useNavigate();
  
  // Step workflow: "aadhar" | "dl" | "review"
  const [scanStep, setScanStep] = useState("aadhar");
  const [extracting, setExtracting] = useState(false);
  const [progressMessage, setProgressMessage] = useState("");
  
  // Image states
  const [aadhaarFrontImage, setAadhaarFrontImage] = useState(null);
  const [aadhaarBackImage, setAadhaarBackImage] = useState(null);
  const [dlFrontImage, setDlFrontImage] = useState(null);
  const [dlBackImage, setDlBackImage] = useState(null);

  // Extracted Data states
  const [extractedAadharData, setExtractedAadharData] = useState(null);
  const [extractedDlData, setExtractedDlData] = useState(null);
  
  const [aadharConfidence, setAadharConfidence] = useState({});
  const [dlConfidence, setDlConfidence] = useState({});
  
  const [aadharReasons, setAadharReasons] = useState({});
  const [dlReasons, setDlReasons] = useState({});

  const [allowChecksumBypass, setAllowChecksumBypass] = useState(false);
  const [existingCandidateModal, setExistingCandidateModal] = useState(null);

  // Active camera state: null | "aadhar_front" | "aadhar_back" | "dl_front" | "dl_back"
  const [cameraTarget, setCameraTarget] = useState(null);
  const [cameraStream, setCameraStream] = useState(null);
  
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach((track) => track.stop());
      }
    };
  }, [cameraStream]);

  const startCameraFor = async (target) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      setCameraStream(stream);
      setCameraTarget(target);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play();
        }
      }, 200);
    } catch (err) {
      console.error(err);
      toast.error("Camera access denied");
    }
  };

  const captureImageForTarget = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    const scale = 2;
    canvas.width = video.videoWidth * scale;
    canvas.height = video.videoHeight * scale;

    const ctx = canvas.getContext("2d");
    ctx.scale(scale, scale);
    ctx.drawImage(video, 0, 0, video.videoWidth, video.videoHeight);

    canvas.toBlob((blob) => {
      const filename = `${cameraTarget}.jpg`;
      const file = new File([blob], filename, { type: "image/jpeg" });
      
      if (cameraTarget === "aadhar_front") setAadhaarFrontImage(file);
      else if (cameraTarget === "aadhar_back") setAadhaarBackImage(file);
      else if (cameraTarget === "dl_front") setDlFrontImage(file);
      else if (cameraTarget === "dl_back") setDlBackImage(file);
      
      stopCamera();
    }, "image/jpeg", 1);
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
    }
    setCameraStream(null);
    setCameraTarget(null);
  };

  // Helper for photo counts
  const aadhaarPhotoCount = (aadhaarFrontImage ? 1 : 0) + (aadhaarBackImage ? 1 : 0);
  const dlPhotoCount = (dlFrontImage ? 1 : 0) + (dlBackImage ? 1 : 0);

  // --- Process Aadhaar Extraction ---
  const processAadhaarExtraction = async () => {
    if (!aadhaarFrontImage && !aadhaarBackImage) {
      toast.error("Please add at least one Aadhaar photo (Front or Back)");
      return;
    }
    const startTime = Date.now();
    safeLogClientAction("AADHAAR_SCAN_STARTED", "Aadhaar", { source: "scan" });
    safeLogClientAction("AADHAAR_OCR_STARTED", "Aadhaar");
    try {
      setExtracting(true);
      setProgressMessage("Processing Aadhaar image(s)... Please wait...");

      let frontParsed = null;
      let backParsed = null;

      if (aadhaarFrontImage) {
        const frontResponse = await extractAadhaarFromImage(aadhaarFrontImage);
        frontParsed = parseAadhaarResponse(frontResponse);
      }

      if (aadhaarBackImage) {
        setProgressMessage("Processing Aadhaar Back... Merging fields...");
        const backResponse = await extractAadhaarFromImage(aadhaarBackImage);
        backParsed = parseAadhaarResponse(backResponse);
      }

      let mergedResult = frontParsed || backParsed;
      if (frontParsed && backParsed) {
        mergedResult = mergeAadhaarOcrData(frontParsed, backParsed);
      }

      const fields = mergedResult.fields;
      const scores = mergedResult.scores;

      const confObj = {};
      const reasonObj = {};
      Object.keys(fields).forEach((key) => {
        const res = scoreToStatus(scores[key], fields[key], key);
        confObj[key] = res.status;
        reasonObj[key] = res.reason;
      });

      setExtractedAadharData(fields);
      setAadharConfidence(confObj);
      setAadharReasons(reasonObj);

      const cleanNum = fields.aadharNumber ? String(fields.aadharNumber).replace(/\D/g, "") : "";
      if (cleanNum.length === 12) {
        try {
          const checkRes = await checkAadhar(cleanNum);
          if (checkRes.data?.exists && checkRes.data?.candidate) {
            setExistingCandidateModal(checkRes.data.candidate);
            toast.error(`Aadhaar ${cleanNum} is already registered!`);
          }
        } catch (chkErr) {
          console.warn("Aadhaar background check error:", chkErr);
        }
      }

      toast.success("Aadhaar OCR complete!");
      const duration = Date.now() - startTime;
      safeLogClientAction("AADHAAR_OCR_COMPLETED", "Aadhaar", { durationMs: duration, success: true });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Aadhaar OCR processing failed");
      const duration = Date.now() - startTime;
      safeLogClientAction("AADHAAR_OCR_FAILED", "Aadhaar", { durationMs: duration, success: false, error: err.message });
    } finally {
      setExtracting(false);
      setProgressMessage("");
    }
  };

  // --- Process DL Extraction ---
  const processDlExtraction = async () => {
    if (!dlFrontImage && !dlBackImage) {
      toast.error("Please add at least one Driving Licence photo (Front or Back)");
      return;
    }
    const startTime = Date.now();
    safeLogClientAction("DL_SCAN_STARTED", "Driving Licence", { source: "scan" });
    safeLogClientAction("DL_OCR_STARTED", "Driving Licence");
    try {
      setExtracting(true);
      setProgressMessage("Processing Driving Licence image(s)... Please wait...");

      let frontParsed = null;
      let backParsed = null;

      if (dlFrontImage) {
        const frontResponse = await extractDlFromImage(dlFrontImage);
        console.log("DL OCR API response (Front):", frontResponse);
        frontParsed = parseDlResponse(frontResponse);
      }

      if (dlBackImage) {
        setProgressMessage("Processing Driving Licence Back... Merging fields...");
        const backResponse = await extractDlFromImage(dlBackImage);
        console.log("DL OCR API response (Back):", backResponse);
        backParsed = parseDlResponse(backResponse);
      }

      let mergedResult = frontParsed || backParsed;
      if (frontParsed && backParsed) {
        mergedResult = mergeDlOcrData(frontParsed, backParsed);
      }

      const dlFields = mergedResult.dlFields;
      const dlScores = mergedResult.dlScores;
      const extra = mergedResult.candidateExtra;

      console.log("Mapped extractedDlData:", dlFields);

      const confObj = {};
      const reasonObj = {};
      Object.keys(dlFields).forEach((key) => {
        const res = scoreToStatus(dlScores[key], dlFields[key], key);
        confObj[key] = res.status;
        reasonObj[key] = res.reason;
      });

      setExtractedDlData(dlFields);
      setDlConfidence(confObj);
      setDlReasons(reasonObj);

      // Merge candidate info if missing or suspicious in Aadhaar section
      setExtractedAadharData((prev) => ({
        ...prev,
        name: (prev?.name && !isSuspiciousDlName(prev.name)) ? prev.name : (extra.name || prev?.name || ""),
        fathersName: prev?.fathersName || extra.fathersName || "",
        dob: prev?.dob || extra.dob || ""
      }));

      toast.success("Driving Licence OCR complete!");
      const duration = Date.now() - startTime;
      safeLogClientAction("DL_OCR_COMPLETED", "Driving Licence", { durationMs: duration, success: true });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "DL OCR processing failed");
      const duration = Date.now() - startTime;
      safeLogClientAction("DL_OCR_FAILED", "Driving Licence", { durationMs: duration, success: false, error: err.message });
    } finally {
      setExtracting(false);
      setProgressMessage("");
    }
  };

  const handleConfirmAndContinue = async () => {
    const rawAadhaar = extractedAadharData?.aadharNumber || "";
    const cleanAadhaar = String(rawAadhaar).replace(/\D/g, "").trim();

    if (!cleanAadhaar) {
      toast.error("Aadhaar Number is missing!");
      return;
    }

    if (cleanAadhaar.length !== 12) {
      toast.error(`Aadhaar Number must be exactly 12 digits (currently ${cleanAadhaar.length} digits)`);
      return;
    }

    const isChecksumValid = validateVerhoeff(cleanAadhaar);
    if (!isChecksumValid) {
      if (aadharConfidence.aadharNumber !== "Manually Verified" && !allowChecksumBypass) {
        setAadharConfidence(prev => ({ ...prev, aadharNumber: "Needs Verification" }));
        setAadharReasons(prev => ({ ...prev, aadharNumber: "Checksum mismatch" }));
        setAllowChecksumBypass(true);
        toast.error("Aadhaar checksum mismatch! Please verify the 12 digits, or click Continue again to confirm.");
        return;
      }
    }

    try {
      setExtracting(true);
      setProgressMessage("Verifying details with registry...");
      const res = await checkAadhar(cleanAadhaar);
      if (res.data.exists) {
        if (res.data.candidate) {
          setExistingCandidateModal(res.data.candidate);
        }
        toast.error("Candidate already registered with this Aadhaar!");
        return;
      }

      const finalCandidate = {
        ...extractedAadharData,
        aadharNumber: cleanAadhaar,
        ...extractedDlData,
      };

      localStorage.setItem("ocrCandidateData", JSON.stringify(finalCandidate));
      toast.success("Details verified! Pre-filling registration...");
      navigate("/candidates", {
        state: {
          aadharFile: aadhaarFrontImage || null,
          aadharBackFile: aadhaarBackImage || null,
          dlFile: dlFrontImage || null,
          dlBackFile: dlBackImage || null,
        }
      });
    } catch (err) {
      console.error(err);
      toast.error("Aadhaar verification request failed.");
    } finally {
      setExtracting(false);
      setProgressMessage("");
    }
  };

  const renderConfidenceBadge = (status, reason) => {
    if (status === "High Confidence") {
      return (
        <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--teal)", background: "var(--teal-soft)", padding: "3px 9px", borderRadius: "10px", border: "1px solid rgba(31,111,100,0.2)", marginLeft: "8px" }}>
          ✓ High Confidence
        </span>
      );
    }
    if (status === "Medium Confidence") {
      return (
        <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--brass-deep)", background: "var(--brass-soft)", padding: "3px 9px", borderRadius: "10px", border: "1px solid rgba(184,134,46,0.3)", marginLeft: "8px" }}>
          • Medium Confidence
        </span>
      );
    }
    if (status === "Manually Verified") {
      return (
        <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--slate)", background: "#F1F5F9", padding: "3px 9px", borderRadius: "10px", border: "1px solid var(--hairline)", marginLeft: "8px" }}>
          ✓ Manually Verified
        </span>
      );
    }
    return (
      <span style={{ fontSize: "11px", fontWeight: "700", color: "var(--rose)", background: "var(--rose-soft)", padding: "3px 9px", borderRadius: "10px", border: "1px solid rgba(178,58,72,0.2)", marginLeft: "8px" }}>
        ⚠ {reason || "Needs Verification"}
      </span>
    );
  };

  const stepsList = [
    { key: "aadhar", label: "Aadhaar Card" },
    { key: "dl", label: "Driving Licence" },
    { key: "review", label: "Review Extracted Data" },
  ];

  const currentStepIdx = stepsList.findIndex(s => s.key === scanStep);

  const formatAadhaarDisplay = (numStr) => {
    if (!numStr) return "";
    const clean = String(numStr).replace(/\D/g, "");
    if (clean.length === 12) {
      return `${clean.slice(0, 4)} ${clean.slice(4, 8)} ${clean.slice(8, 12)}`;
    }
    return numStr;
  };

  const renderLiveCameraHUD = (targetTitle) => {
    return (
      <div 
        className="camera-view" 
        onClick={captureImageForTarget} 
        title="Tap anywhere to capture photo"
      >
        <video 
          ref={videoRef} 
          autoPlay 
          playsInline 
          style={{ width: "100%", height: "100%", objectFit: "cover" }} 
        />
        <canvas ref={canvasRef} style={{ display: "none" }} />
        <div className="scan-hud-overlay">
          <div className="scan-badge">
            <span style={{ display: "inline-block", width: "6px", height: "6px", borderRadius: "50%", background: "#2BB5CE" }}></span>
            Scanning Live ({targetTitle})...
          </div>
          <div style={{ fontSize: "11px", color: "#FFFFFF", fontWeight: "600", textShadow: "0 1px 3px rgba(0,0,0,0.8)", opacity: 0.9 }}>
            Tap anywhere to capture photo
          </div>
        </div>
        <div className="scan-laser-line"></div>
        <div className="scan-corner scan-corner-tl"></div>
        <div className="scan-corner scan-corner-tr"></div>
        <div className="scan-corner scan-corner-bl"></div>
        <div className="scan-corner scan-corner-br"></div>
      </div>
    );
  };

  return (
    <>
      <style>{styles}</style>
      <div className="dash-root">
        
        {/* PAGE HEADER */}
        <div className="app-header">
          <div>
            <div className="app-tag">DOCUMENT INTELLIGENCE · OCR REGISTRY</div>
            <div className="app-title">Scan &amp; Extract Documents</div>
            <div className="app-subtitle">Capture document images and automatically extract candidate information.</div>
          </div>
          <div style={{ display: "flex", alignItems: "center" }}>
            <div className="engine-badge">
              <span className="status-dot-green"></span>
              <span>OCR ENGINE ONLINE</span>
            </div>
            <button className="btn-compact-back" onClick={() => navigate("/candidates")} disabled={extracting}>
              <FaChevronLeft style={{ fontSize: "10px" }} /> Back to Candidates
            </button>
          </div>
        </div>

        {/* WORKFLOW STEP NAVIGATION */}
        <div className="workflow-nav">
          {[
            { key: "aadhar", label: "Aadhaar Document", sub: "Capture identity document" },
            { key: "dl", label: "Driving Licence", sub: "Optional document" },
            { key: "review", label: "Review & Verify", sub: "Confirm extracted information" },
          ].map((step, idx) => {
            const isActive = currentStepIdx === idx;
            const isCompleted = currentStepIdx > idx;
            return (
              <div 
                key={step.key} 
                className={`workflow-step ${isActive ? "active" : ""} ${isCompleted ? "completed" : ""}`}
                onClick={() => setScanStep(step.key)}
              >
                <div className="workflow-dot" />
                <div style={{ flex: 1 }}>
                  <div className="workflow-title">{step.label}</div>
                  <div className="workflow-sub">
                    {isCompleted ? "✓ Complete" : isActive ? "Current Step" : step.sub}
                  </div>
                </div>
                {isCompleted && <FaCheckCircle style={{ color: "var(--wb-teal)", fontSize: "14px" }} />}
              </div>
            );
          })}
        </div>

        {/* WORKBENCH CONTAINER */}
        <div className="workbench-container">

          {/* ================= STEP 1: AADHAAR CARD ================= */}
          {scanStep === "aadhar" && (
            <div>
              <div className="section-banner">
                <div>
                  <div className="section-heading">AADHAAR DOCUMENT</div>
                  <div className="section-desc">Upload one or both sides of the candidate's Aadhaar card.</div>
                  <div style={{ fontSize: "12px", color: "var(--wb-teal)", marginTop: "4px", fontWeight: "600" }}>
                    Front is recommended. Back is optional and can provide additional address information.
                  </div>
                </div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>
                  DOCUMENT TRAY 1 OF 2
                </div>
              </div>

              {/* DOCUMENT TRAYS GRID */}
              <div className="tray-grid">
                
                {/* FRONT TRAY */}
                <div className={`document-tray ${aadhaarFrontImage ? "captured" : ""}`}>
                  <div className="tray-header">
                    <span className="tray-title">AADHAAR FRONT</span>
                    {aadhaarFrontImage ? (
                      <span className="tray-badge-captured"><FaCheckCircle /> CAPTURED</span>
                    ) : (
                      <span className="tray-badge-ready">● READY</span>
                    )}
                  </div>

                  <div className="tray-viewzone">
                    {aadhaarFrontImage ? (
                      <div style={{ position: "relative", width: "100%", height: "100%" }}>
                        <img src={URL.createObjectURL(aadhaarFrontImage)} alt="Aadhaar Front" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <button 
                          onClick={() => setAadhaarFrontImage(null)}
                          style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(220,38,38,0.9)", color: "#fff", border: "none", borderRadius: "50%", width: "26px", height: "26px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          title="Remove Photo"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <FaFileAlt style={{ fontSize: "32px", color: "var(--wb-muted)", marginBottom: "10px" }} />
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--wb-text)" }}>Drop Aadhaar front image here</div>
                        <div style={{ fontSize: "11.5px", color: "var(--wb-muted)", marginTop: "2px" }}>or click below to scan / upload</div>
                      </>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button className="btn-tray-scan" onClick={() => startCameraFor("aadhar_front")} disabled={extracting}>
                      <FaCamera /> SCAN WITH CAMERA
                    </button>
                    <label className="btn-tray-upload" style={{ opacity: extracting ? 0.6 : 1, cursor: extracting ? "not-allowed" : "pointer" }}>
                      <FaUpload /> UPLOAD IMAGE
                      <input 
                        type="file" 
                        hidden 
                        accept="image/*" 
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setAadhaarFrontImage(e.target.files[0]);
                          }
                        }}
                        disabled={extracting}
                      />
                    </label>
                  </div>
                </div>

                {/* BACK TRAY */}
                <div className={`document-tray ${aadhaarBackImage ? "captured" : ""}`}>
                  <div className="tray-header">
                    <span className="tray-title">AADHAAR BACK</span>
                    {aadhaarBackImage ? (
                      <span className="tray-badge-captured"><FaCheckCircle /> CAPTURED</span>
                    ) : (
                      <span className="tray-badge-ready" style={{ background: "#F1F5F9" }}>OPTIONAL</span>
                    )}
                  </div>

                  <div className="tray-viewzone">
                    {aadhaarBackImage ? (
                      <div style={{ position: "relative", width: "100%", height: "100%" }}>
                        <img src={URL.createObjectURL(aadhaarBackImage)} alt="Aadhaar Back" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <button 
                          onClick={() => setAadhaarBackImage(null)}
                          style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(220,38,38,0.9)", color: "#fff", border: "none", borderRadius: "50%", width: "26px", height: "26px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          title="Remove Photo"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <FaFileAlt style={{ fontSize: "32px", color: "var(--wb-muted)", marginBottom: "10px" }} />
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--wb-text)" }}>Optional — add back side</div>
                        <div style={{ fontSize: "11.5px", color: "var(--wb-muted)", marginTop: "2px" }}>Provides additional address details</div>
                      </>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button className="btn-tray-scan" onClick={() => startCameraFor("aadhar_back")} disabled={extracting}>
                      <FaCamera /> SCAN WITH CAMERA
                    </button>
                    <label className="btn-tray-upload" style={{ opacity: extracting ? 0.6 : 1, cursor: extracting ? "not-allowed" : "pointer" }}>
                      <FaUpload /> UPLOAD IMAGE
                      <input 
                        type="file" 
                        hidden 
                        accept="image/*" 
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setAadhaarBackImage(e.target.files[0]);
                          }
                        }}
                        disabled={extracting}
                      />
                    </label>
                  </div>
                </div>

              </div>

              {/* EXTRACTION ACTION BAR */}
              <div className="extraction-bar">
                <div>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DOCUMENT STATUS</div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--wb-text)" }}>
                    {aadhaarPhotoCount === 0 ? "No document images added yet" : `✓ ${aadhaarPhotoCount} ${aadhaarPhotoCount === 1 ? "side" : "sides"} captured`}
                  </div>
                </div>

                <button 
                  className="btn-extract-primary"
                  disabled={aadhaarPhotoCount === 0 || extracting}
                  onClick={processAadhaarExtraction}
                >
                  {extracting ? (
                    <>
                      <CircularProgress size={16} color="inherit" />
                      <span>{progressMessage || "OCR PROCESSING..."}</span>
                    </>
                  ) : (
                    <>
                      <FaFingerprint />
                      <span>EXTRACT DOCUMENT DATA →</span>
                    </>
                  )}
                </button>
              </div>

              {/* QUICK RESULT STRIP */}
              {extractedAadharData?.aadharNumber && (
                <div className="quick-result-strip">
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>EXTRACTED IDENTIFIER · AADHAAR NUMBER</div>
                    <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--wb-text)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.06em", marginTop: "2px" }}>
                      {formatAadhaarDisplay(extractedAadharData.aadharNumber)}
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
                    {renderConfidenceBadge(aadharConfidence.aadharNumber, aadharReasons.aadharNumber)}
                    <button 
                      className="btn-action-primary"
                      onClick={() => setScanStep("dl")}
                    >
                      CONTINUE TO DRIVING LICENCE <FaArrowRight />
                    </button>
                  </div>
                </div>
              )}

            </div>
          )}

          {/* ================= STEP 2: DRIVING LICENCE ================= */}
          {scanStep === "dl" && (
            <div>
              <div className="section-banner">
                <div>
                  <div className="section-heading">DRIVING LICENCE</div>
                  <div className="section-desc">Optional document · Add the front and/or back side if available.</div>
                  <div style={{ fontSize: "12px", color: "var(--wb-muted)", marginTop: "4px" }}>
                    You can continue without a Driving Licence.
                  </div>
                </div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>
                  DOCUMENT TRAY 2 OF 2
                </div>
              </div>

              {/* TRAYS GRID */}
              <div className="tray-grid">
                
                {/* FRONT TRAY */}
                <div className={`document-tray ${dlFrontImage ? "captured" : ""}`}>
                  <div className="tray-header">
                    <span className="tray-title">DL FRONT</span>
                    {dlFrontImage ? (
                      <span className="tray-badge-captured"><FaCheckCircle /> CAPTURED</span>
                    ) : (
                      <span className="tray-badge-ready">● READY</span>
                    )}
                  </div>

                  <div className="tray-viewzone">
                    {dlFrontImage ? (
                      <div style={{ position: "relative", width: "100%", height: "100%" }}>
                        <img src={URL.createObjectURL(dlFrontImage)} alt="DL Front" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <button 
                          onClick={() => setDlFrontImage(null)}
                          style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(220,38,38,0.9)", color: "#fff", border: "none", borderRadius: "50%", width: "26px", height: "26px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          title="Remove Photo"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <FaFileAlt style={{ fontSize: "32px", color: "var(--wb-muted)", marginBottom: "10px" }} />
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--wb-text)" }}>Scan or upload DL Front</div>
                        <div style={{ fontSize: "11.5px", color: "var(--wb-muted)", marginTop: "2px" }}>Captures Licence No &amp; Issue details</div>
                      </>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button className="btn-tray-scan" onClick={() => startCameraFor("dl_front")} disabled={extracting}>
                      <FaCamera /> SCAN WITH CAMERA
                    </button>
                    <label className="btn-tray-upload" style={{ opacity: extracting ? 0.6 : 1, cursor: extracting ? "not-allowed" : "pointer" }}>
                      <FaUpload /> UPLOAD IMAGE
                      <input 
                        type="file" 
                        hidden 
                        accept="image/*" 
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setDlFrontImage(e.target.files[0]);
                          }
                        }}
                        disabled={extracting}
                      />
                    </label>
                  </div>
                </div>

                {/* BACK TRAY */}
                <div className={`document-tray ${dlBackImage ? "captured" : ""}`}>
                  <div className="tray-header">
                    <span className="tray-title">DL BACK</span>
                    {dlBackImage ? (
                      <span className="tray-badge-captured"><FaCheckCircle /> CAPTURED</span>
                    ) : (
                      <span className="tray-badge-ready" style={{ background: "#F1F5F9" }}>OPTIONAL</span>
                    )}
                  </div>

                  <div className="tray-viewzone">
                    {dlBackImage ? (
                      <div style={{ position: "relative", width: "100%", height: "100%" }}>
                        <img src={URL.createObjectURL(dlBackImage)} alt="DL Back" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                        <button 
                          onClick={() => setDlBackImage(null)}
                          style={{ position: "absolute", top: "8px", right: "8px", background: "rgba(220,38,38,0.9)", color: "#fff", border: "none", borderRadius: "50%", width: "26px", height: "26px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                          title="Remove Photo"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ) : (
                      <>
                        <FaFileAlt style={{ fontSize: "32px", color: "var(--wb-muted)", marginBottom: "10px" }} />
                        <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--wb-text)" }}>Optional — add back side</div>
                        <div style={{ fontSize: "11.5px", color: "var(--wb-muted)", marginTop: "2px" }}>Provides additional endorsement details</div>
                      </>
                    )}
                  </div>

                  <div style={{ display: "flex", gap: "10px" }}>
                    <button className="btn-tray-scan" onClick={() => startCameraFor("dl_back")} disabled={extracting}>
                      <FaCamera /> SCAN WITH CAMERA
                    </button>
                    <label className="btn-tray-upload" style={{ opacity: extracting ? 0.6 : 1, cursor: extracting ? "not-allowed" : "pointer" }}>
                      <FaUpload /> UPLOAD IMAGE
                      <input 
                        type="file" 
                        hidden 
                        accept="image/*" 
                        onChange={(e) => {
                          if (e.target.files && e.target.files[0]) {
                            setDlBackImage(e.target.files[0]);
                          }
                        }}
                        disabled={extracting}
                      />
                    </label>
                  </div>
                </div>

              </div>

              {/* EXTRACTION ACTION BAR */}
              <div className="extraction-bar">
                <div>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DL DOCUMENT STATUS</div>
                  <div style={{ fontSize: "13px", fontWeight: "700", color: "var(--wb-text)" }}>
                    {dlPhotoCount === 0 ? "No DL images added (Optional)" : `✓ ${dlPhotoCount} ${dlPhotoCount === 1 ? "side" : "sides"} captured`}
                  </div>
                </div>

                <button 
                  className="btn-extract-primary"
                  disabled={dlPhotoCount === 0 || extracting}
                  onClick={processDlExtraction}
                >
                  {extracting ? (
                    <>
                      <CircularProgress size={16} color="inherit" />
                      <span>{progressMessage || "OCR PROCESSING..."}</span>
                    </>
                  ) : (
                    <>
                      <FaFingerprint />
                      <span>EXTRACT DOCUMENT DATA →</span>
                    </>
                  )}
                </button>
              </div>

              {/* QUICK RESULT STRIP */}
              {extractedDlData?.dlNo && (
                <div className="quick-result-strip">
                  <div>
                    <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>EXTRACTED LICENCE NUMBER</div>
                    <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--wb-text)", fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.06em", marginTop: "2px" }}>
                      {extractedDlData.dlNo}
                    </div>
                  </div>
                  <div>
                    {renderConfidenceBadge(dlConfidence.dlNo, dlReasons.dlNo)}
                  </div>
                </div>
              )}

              {/* NAVIGATION FOOTER */}
              <div className="bottom-sticky-bar">
                <button className="btn-action-outline" onClick={() => setScanStep("aadhar")} disabled={extracting}>
                  <FaChevronLeft /> Back to Aadhaar
                </button>
                <button className="btn-action-primary" onClick={() => setScanStep("review")}>
                  CONTINUE TO REVIEW <FaArrowRight />
                </button>
              </div>

            </div>
          )}

          {/* ================= STEP 3: REVIEW & VERIFY ================= */}
          {scanStep === "review" && (
            <div>
              <div className="section-banner" style={{ marginBottom: "16px" }}>
                <div>
                  <div className="section-heading">REVIEW EXTRACTED INFORMATION</div>
                  <div className="section-desc">Check the extracted information before continuing to registration.</div>
                </div>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--wb-teal)", fontFamily: "'IBM Plex Mono', monospace", display: "flex", alignItems: "center", gap: "6px" }}>
                  <FaShieldAlt /> VERIFICATION WORKBENCH
                </div>
              </div>

              {/* CANDIDATE IDENTITY SUMMARY BOX */}
              <div className="review-summary-box">
                <div>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>CANDIDATE IDENTITY SUMMARY</div>
                  <div style={{ fontSize: "18px", fontWeight: "800", color: "var(--wb-text)", marginTop: "2px" }}>
                    {extractedAadharData?.name || "Candidate Name Not Extracted"}
                  </div>
                  <div style={{ fontSize: "12.5px", color: "var(--wb-muted)", marginTop: "2px" }}>
                    Aadhaar: <strong>{formatAadhaarDisplay(extractedAadharData?.aadharNumber) || "N/A"}</strong> · DOB: <strong>{extractedAadharData?.dob || "N/A"}</strong>
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>OVERALL OCR STATUS</div>
                  <div style={{ marginTop: "4px" }}>
                    {renderConfidenceBadge(aadharConfidence.aadharNumber, aadharReasons.aadharNumber)}
                  </div>
                </div>
              </div>

              {/* REVIEW GRID (65% Identity, 35% Driving Licence) */}
              <div className="review-grid">
                
                {/* AADHAAR IDENTITY PANEL (65%) */}
                <div className="review-panel">
                  <div className="review-panel-title">
                    <span>🆔 AADHAAR IDENTITY FIELDS</span>
                    <span style={{ fontSize: "11px", color: "var(--wb-muted)", fontWeight: "600" }}>PRIMARY RECORD</span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    
                    {/* AADHAAR NUMBER */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>AADHAAR NUMBER</label>
                        {renderConfidenceBadge(aadharConfidence.aadharNumber, aadharReasons.aadharNumber)}
                      </div>
                      <input 
                        className="input-field" 
                        style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700, fontSize: "14px", letterSpacing: "0.05em" }}
                        value={extractedAadharData?.aadharNumber || ""} 
                        onChange={(e) => {
                          setExtractedAadharData({ ...extractedAadharData, aadharNumber: e.target.value });
                          setAadharConfidence({ ...aadharConfidence, aadharNumber: "Manually Verified" });
                          setAadharReasons({ ...aadharReasons, aadharNumber: "" });
                        }}
                      />
                    </div>

                    {/* NAME */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>CANDIDATE NAME</label>
                        {renderConfidenceBadge(aadharConfidence.name, aadharReasons.name)}
                      </div>
                      <input 
                        className="input-field" 
                        style={{ fontWeight: 700 }}
                        value={extractedAadharData?.name || ""} 
                        onChange={(e) => {
                          setExtractedAadharData({ ...extractedAadharData, name: e.target.value });
                          setAadharConfidence({ ...aadharConfidence, name: "Manually Verified" });
                          setAadharReasons({ ...aadharReasons, name: "" });
                        }}
                      />
                    </div>

                    {/* FATHER'S NAME */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>FATHER'S NAME</label>
                        {renderConfidenceBadge(aadharConfidence.fathersName, aadharReasons.fathersName)}
                      </div>
                      <input 
                        className="input-field" 
                        value={extractedAadharData?.fathersName || ""} 
                        onChange={(e) => {
                          setExtractedAadharData({ ...extractedAadharData, fathersName: e.target.value });
                          setAadharConfidence({ ...aadharConfidence, fathersName: "Manually Verified" });
                          setAadharReasons({ ...aadharReasons, fathersName: "" });
                        }}
                      />
                    </div>

                    {/* DOB & GENDER (Segmented buttons for gender) */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                          <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DATE OF BIRTH</label>
                        </div>
                        <input 
                          type="text" 
                          className="input-field" 
                          placeholder="YYYY-MM-DD"
                          value={extractedAadharData?.dob || ""} 
                          onChange={(e) => {
                            setExtractedAadharData({ ...extractedAadharData, dob: e.target.value });
                            setAadharConfidence({ ...aadharConfidence, dob: "Manually Verified" });
                            setAadharReasons({ ...aadharReasons, dob: "" });
                          }}
                        />
                      </div>

                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                          <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>GENDER</label>
                        </div>
                        <div className="segmented-gender">
                          {["Male", "Female", "Other"].map((g) => (
                            <button
                              key={g}
                              type="button"
                              className={`segmented-btn ${extractedAadharData?.gender === g ? "active" : ""}`}
                              onClick={() => {
                                setExtractedAadharData({ ...extractedAadharData, gender: g });
                                setAadharConfidence({ ...aadharConfidence, gender: "Manually Verified" });
                                setAadharReasons({ ...aadharReasons, gender: "" });
                              }}
                            >
                              {g}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* CONTACT & PINCODE */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                          <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>MOBILE NUMBER</label>
                        </div>
                        <input 
                          className="input-field" 
                          value={extractedAadharData?.contactNumber || ""} 
                          onChange={(e) => {
                            setExtractedAadharData({ ...extractedAadharData, contactNumber: e.target.value });
                            setAadharConfidence({ ...aadharConfidence, contactNumber: "Manually Verified" });
                            setAadharReasons({ ...aadharReasons, contactNumber: "" });
                          }}
                        />
                      </div>
                      <div>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                          <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>PIN CODE</label>
                        </div>
                        <input 
                          className="input-field" 
                          style={{ fontFamily: "'IBM Plex Mono', monospace" }}
                          value={extractedAadharData?.pincode || ""} 
                          onChange={(e) => {
                            setExtractedAadharData({ ...extractedAadharData, pincode: e.target.value });
                            setAadharConfidence({ ...aadharConfidence, pincode: "Manually Verified" });
                            setAadharReasons({ ...aadharReasons, pincode: "" });
                          }}
                        />
                      </div>
                    </div>

                    {/* PERMANENT ADDRESS */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>PERMANENT ADDRESS</label>
                        {renderConfidenceBadge(aadharConfidence.address, aadharReasons.address)}
                      </div>
                      <textarea 
                        className="input-field" 
                        rows={3}
                        value={extractedAadharData?.address || ""} 
                        onChange={(e) => {
                          setExtractedAadharData({ ...extractedAadharData, address: e.target.value });
                          setAadharConfidence({ ...aadharConfidence, address: "Manually Verified" });
                          setAadharReasons({ ...aadharReasons, address: "" });
                        }}
                        style={{ resize: "none" }}
                      />
                    </div>

                  </div>
                </div>

                {/* LICENCE SNAPSHOT PANEL (35%) */}
                <div className="review-panel">
                  <div className="review-panel-title">
                    <span>🚗 LICENCE SNAPSHOT</span>
                    <span style={{ fontSize: "11px", color: "var(--wb-muted)", fontWeight: "600" }}>OPTIONAL</span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                    
                    {/* DL NUMBER */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DL NUMBER</label>
                        {renderConfidenceBadge(dlConfidence.dlNo, dlReasons.dlNo)}
                      </div>
                      <input 
                        className="input-field" 
                        style={{ fontFamily: "'IBM Plex Mono', monospace", fontWeight: 700 }}
                        value={extractedDlData?.dlNo || ""} 
                        onChange={(e) => {
                          setExtractedDlData({ ...extractedDlData, dlNo: e.target.value });
                          setDlConfidence({ ...dlConfidence, dlNo: "Manually Verified" });
                          setDlReasons({ ...dlReasons, dlNo: "" });
                        }}
                        placeholder="Not Provided"
                      />
                    </div>

                    {/* DL TYPE / CATEGORY SUMMARY */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DL CATEGORY / TYPE</label>
                        {renderConfidenceBadge(dlConfidence.dlType, dlReasons.dlType)}
                      </div>
                      <input 
                        className="input-field" 
                        value={extractedDlData?.dlType || ""} 
                        onChange={(e) => {
                          setExtractedDlData({ ...extractedDlData, dlType: e.target.value });
                          setDlConfidence({ ...dlConfidence, dlType: "Manually Verified" });
                          setDlReasons({ ...dlReasons, dlType: "" });
                        }}
                        placeholder="e.g. LMV, MCWG, TRANS"
                      />
                    </div>

                    {/* VEHICLE CLASS BREAKDOWN TABLE */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "6px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>VEHICLE CATEGORIES & ISSUE DATES</label>
                      </div>
                      {(() => {
                        const entries = Array.isArray(extractedDlData?.vehicle_class_entries)
                          ? extractedDlData.vehicle_class_entries
                          : [];
                        
                        const classes = Array.isArray(extractedDlData?.vehicle_classes)
                          ? extractedDlData.vehicle_classes
                          : [];

                        if (entries.length === 0 && classes.length === 0) {
                          return (
                            <div style={{ fontSize: "12px", color: "var(--wb-muted)", fontStyle: "italic" }}>
                              No vehicle categories detected
                            </div>
                          );
                        }

                        return (
                          <div style={{ background: "#F8FAFC", borderRadius: "8px", border: "1px solid var(--wb-border)", padding: "8px", overflowX: "auto" }}>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                              <thead>
                                <tr style={{ borderBottom: "1px solid var(--wb-border)", textAlign: "left" }}>
                                  <th style={{ padding: "4px 8px", fontSize: "10.5px", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>CLASS</th>
                                  <th style={{ padding: "4px 8px", fontSize: "10.5px", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>COV DESCRIPTION</th>
                                  <th style={{ padding: "4px 8px", fontSize: "10.5px", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace", textAlign: "right" }}>ISSUE DATE</th>
                                </tr>
                              </thead>
                              <tbody>
                                {entries.length > 0 ? (
                                  entries.map((item, idx) => (
                                    <tr key={idx} style={{ borderBottom: idx < entries.length - 1 ? "1px dashed #E2E8F0" : "none" }}>
                                      <td style={{ padding: "6px 8px", fontWeight: "700", fontFamily: "'IBM Plex Mono', monospace", color: "var(--wb-teal)" }}>
                                        {item.class}
                                      </td>
                                      <td style={{ padding: "6px 8px", color: "var(--wb-text)" }}>
                                        {item.cov || item.class}
                                      </td>
                                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono', monospace", fontWeight: "600" }}>
                                        {item.issue_date || "-"}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  classes.map((cls, idx) => (
                                    <tr key={idx} style={{ borderBottom: idx < classes.length - 1 ? "1px dashed #E2E8F0" : "none" }}>
                                      <td style={{ padding: "6px 8px", fontWeight: "700", fontFamily: "'IBM Plex Mono', monospace", color: "var(--wb-teal)" }}>
                                        {cls}
                                      </td>
                                      <td style={{ padding: "6px 8px", color: "var(--wb-text)" }}>
                                        {cls}
                                      </td>
                                      <td style={{ padding: "6px 8px", textAlign: "right", fontFamily: "'IBM Plex Mono', monospace", fontWeight: "600" }}>
                                        {extractedDlData?.dlIssueDate || "-"}
                                      </td>
                                    </tr>
                                  ))
                                )}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>

                    {/* DATES */}
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" }}>
                      <div>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DL ISSUE DATE</label>
                        <input 
                          type="text" 
                          className="input-field" 
                          placeholder="YYYY-MM-DD"
                          value={extractedDlData?.dlIssueDate || ""} 
                          onChange={(e) => {
                            setExtractedDlData({ ...extractedDlData, dlIssueDate: e.target.value });
                            setDlConfidence({ ...dlConfidence, dlIssueDate: "Manually Verified" });
                            setDlReasons({ ...dlReasons, dlIssueDate: "" });
                          }}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>DL EXPIRY DATE</label>
                        <input 
                          type="text" 
                          className="input-field" 
                          placeholder="YYYY-MM-DD"
                          value={extractedDlData?.licenseExpiryDate || ""} 
                          onChange={(e) => {
                            setExtractedDlData({ ...extractedDlData, licenseExpiryDate: e.target.value });
                            setDlConfidence({ ...dlConfidence, licenseExpiryDate: "Manually Verified" });
                            setDlReasons({ ...dlReasons, licenseExpiryDate: "" });
                          }}
                        />
                      </div>
                    </div>

                    {/* AUTHORITY */}
                    <div>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "4px" }}>
                        <label style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)", fontFamily: "'IBM Plex Mono', monospace" }}>ISSUING AUTHORITY</label>
                        {renderConfidenceBadge(dlConfidence.dlAuthority, dlReasons.dlAuthority)}
                      </div>
                      <input 
                        className="input-field" 
                        value={extractedDlData?.dlAuthority || ""} 
                        onChange={(e) => {
                          setExtractedDlData({ ...extractedDlData, dlAuthority: e.target.value });
                          setDlConfidence({ ...dlConfidence, dlAuthority: "Manually Verified" });
                          setDlReasons({ ...dlReasons, dlAuthority: "" });
                        }}
                        placeholder="e.g. RTO Vellore"
                      />
                    </div>

                  </div>
                </div>

              </div>

              {/* PROGRESS BAR */}
              {extracting && progressMessage && (
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "20px", padding: "12px 18px", background: "var(--wb-blue-soft)", borderRadius: "6px", border: "1px solid #BFDBFE" }}>
                  <CircularProgress size={18} color="primary" />
                  <span style={{ fontSize: "13px", fontWeight: "600", color: "#1E40AF" }}>{progressMessage}</span>
                </div>
              )}

              {/* BOTTOM ACTION BAR */}
              <div className="bottom-sticky-bar">
                <button className="btn-action-outline" onClick={() => setScanStep("dl")} disabled={extracting}>
                  <FaChevronLeft /> Back to DL Scan
                </button>

                <div style={{ fontSize: "12.5px", fontWeight: "600", color: "var(--wb-muted)" }}>
                  Documents Captured: <strong>{(aadhaarPhotoCount > 0 ? 1 : 0) + (dlPhotoCount > 0 ? 1 : 0)}</strong>
                </div>

                <button 
                  className="btn-action-primary"
                  disabled={extracting} 
                  onClick={handleConfirmAndContinue}
                >
                  {extracting ? "VERIFYING REGISTRY..." : <>CONFIRM &amp; PRE-FILL CANDIDATE <FaCheckCircle /></>}
                </button>
              </div>

            </div>
          )}

        </div>

      </div>

      {/* DUPLICATE CANDIDATE MODAL */}
      {existingCandidateModal && (
        <div className="modal-overlay" onClick={() => setExistingCandidateModal(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setExistingCandidateModal(null)}>
              <FaTimes />
            </button>

            <div style={{ display: "flex", alignItems: "flex-start", gap: "14px", marginBottom: "20px" }}>
              <div style={{
                width: "44px", height: "44px", borderRadius: "8px",
                background: "#FEE2E2", color: "#DC2626",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "20px", flexShrink: 0
              }}>
                <FaInfoCircle />
              </div>
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: "#DC2626", fontFamily: "'IBM Plex Mono', monospace", textTransform: "uppercase" }}>Registry Match Detected</div>
                <div style={{ fontSize: "17px", fontWeight: "800", color: "var(--wb-text)" }}>
                  Candidate Already Registered
                </div>
                <div style={{ fontSize: "12.5px", color: "var(--wb-muted)", marginTop: "2px" }}>
                  Aadhaar number (<strong>{existingCandidateModal.aadharNumber}</strong>) is already recorded in the database.
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", background: "#F8FAFC", padding: "16px", borderRadius: "8px", border: "1px solid var(--wb-border)" }}>
              <div style={{ gridColumn: "span 2" }}>
                <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)" }}>Candidate Name</div>
                <div style={{ fontSize: "15px", fontWeight: "700", color: "var(--wb-text)" }}>{existingCandidateModal.name || "N/A"}</div>
              </div>

              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)" }}>Father's Name</div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--wb-text)" }}>{existingCandidateModal.fathersName || "N/A"}</div>
              </div>

              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: "var(--wb-muted)" }}>Contact Number</div>
                <div style={{ fontSize: "13px", fontWeight: "600", color: "var(--wb-text)" }}>{existingCandidateModal.contactNumber || "N/A"}</div>
              </div>
            </div>

            <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "10px" }}>
              <button className="btn-action-outline" onClick={() => setExistingCandidateModal(null)}>
                Scan Another Card
              </button>
              <button className="btn-action-primary" onClick={() => navigate("/candidates")}>
                View Candidates <FaChevronRight />
              </button>
            </div>
          </div>
        </div>
      )}
      {/* LIVE CAMERA MODAL */}
      <Dialog
        open={Boolean(cameraTarget)}
        onClose={stopCamera}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px", overflow: "hidden" } }}
      >
        <DialogTitle sx={{ fontWeight: 800, fontSize: 16, color: "#0f172a", borderBottom: "1px solid #f1f5f9", py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>Scan Document: {cameraTarget?.replace("_", " ").toUpperCase()}</span>
          <button 
            onClick={stopCamera}
            style={{ background: "transparent", border: "none", cursor: "pointer", color: "#64748b" }}
          >
            <FaTimes size={16} />
          </button>
        </DialogTitle>

        <DialogContent sx={{ p: 0, backgroundColor: "#000", position: "relative", aspectRatio: "4/3", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {cameraTarget && (
            <div style={{ position: "relative", width: "100%", height: "100%", overflow: "hidden" }}>
              <video
                ref={videoRef}
                autoPlay
                playsInline
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <canvas ref={canvasRef} style={{ display: "none" }} />
              
              {/* HUD overlays */}
              <div className="scan-hud-overlay">
                <div className="scan-badge">
                  <span className="status-dot-green"></span>
                  Align document card in frame
                </div>
              </div>
              <div className="scan-laser-line"></div>
              <div className="scan-corner scan-corner-tl" style={{ top: 20, left: 20 }}></div>
              <div className="scan-corner scan-corner-tr" style={{ top: 20, right: 20 }}></div>
              <div className="scan-corner scan-corner-bl" style={{ bottom: 20, left: 20 }}></div>
              <div className="scan-corner scan-corner-br" style={{ bottom: 20, right: 20 }}></div>
            </div>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2.5, borderTop: "1px solid #f1f5f9", justifyContent: "space-between" }}>
          <Button
            onClick={stopCamera}
            variant="outlined"
            color="inherit"
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 600 }}
          >
            Cancel
          </Button>

          <Button
            onClick={captureImageForTarget}
            variant="contained"
            color="primary"
            startIcon={<FaCamera />}
            sx={{ textTransform: "none", borderRadius: "8px", fontWeight: 700, backgroundColor: "var(--wb-teal)", "&:hover": { backgroundColor: "#126366" } }}
          >
            Capture &amp; Confirm
          </Button>
        </DialogActions>
      </Dialog>

    </>
  );
}
